/**
 * TrackerPublicPage.jsx
 * Route: /tracker/:trackerId  — public, no auth required
 *
 * Fixes in this version:
 *  1. computeTotals — sums view_count/like_count/comments from BOTH
 *     tracker.links (current snapshot) AND trackerHistory (all URLs).
 *     History is the authoritative source when present.
 *  2. computeGrowthRate — correctly sums across ALL URLs in history
 *     for each week window, not just one URL's field names.
 *  3. LinkRow — shows creator @handle pulled from tracker.creator_name.
 *  4. Platform compile — when multiple platforms exist, a combined
 *     aggregate row appears above the individual link rows.
 */

import React, { useMemo, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { ThemeProvider } from '@mui/material/styles';
import {
  Box, Typography, Container, Grid, Avatar, Stack,
  Button, CircularProgress, CssBaseline, Toolbar,
} from '@mui/material';
import {
  YouTube as YouTubeIcon,
  MusicVideo as TikTokIcon,
  Instagram as InstagramIcon,
  Link as LinkIcon,
  OpenInNew as OpenInNewIcon,
  Visibility as ViewsIcon,
  ThumbUp as LikesIcon,
  ChatBubble as CommentsIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as BankIcon,
  Description as ContractIcon,
  TrackChanges as TrackerIconMui,
  Search as SearchIcon,
  ContentCut as ClipperIcon,
  ArrowForward as ArrowForwardIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Remove as FlatIcon,
  Share as ShareIcon,
  Person as PersonIcon,
  BarChart as BarChartIcon,
} from '@mui/icons-material';

import { lightTheme } from '../../Utils/baseTheme';
import HomeHeader from '../../Components/HomeHeader';
import PageTitle from '../../Components/PageTitle';
import blitzLogo from '../../Components/globalAssets/blackwhite.png';
import client from '../../API';
import TrackerProgressChart from './TrackerProgressChart';

/* ─────────────────────────────────────────────────────────────────
   CSS injection
───────────────────────────────────────────────────────────────── */
const CSS_ID = 'tpp-v4';
function ensureAssets() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(CSS_ID)) return;
  const s = document.createElement('style');
  s.id = CSS_ID;
  s.textContent = `
    *, *::before, *::after { box-sizing: border-box; }

    @keyframes tpp-up   { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:translateY(0)} }
    @keyframes tpp-in   { from{opacity:0} to{opacity:1} }
    @keyframes tpp-pop  { from{opacity:0;transform:scale(0.97)} to{opacity:1;transform:scale(1)} }
    @keyframes tpp-live { 0%,100%{opacity:1} 50%{opacity:0.4} }

    .anim-up  { animation: tpp-up  0.52s cubic-bezier(.22,1,.36,1) both; }
    .anim-in  { animation: tpp-in  0.42s ease both; }
    .anim-pop { animation: tpp-pop 0.48s cubic-bezier(.22,1,.36,1) both; }

    .stat-card { transition: box-shadow .18s ease, transform .18s ease; }
    .stat-card:hover { transform:translateY(-2px); box-shadow:0 8px 28px rgba(0,0,0,0.09)!important; }

    .link-row { transition: background .14s ease, box-shadow .14s ease; }
    .link-row:hover { background:#F8FAFC!important; box-shadow:0 2px 12px rgba(0,0,0,0.06)!important; }

    .plat-tab { transition: background .14s ease, color .14s ease, border-color .14s ease; cursor:pointer; }
    .plat-tab.active { background:#0F172A!important; color:#fff!important; border-color:#0F172A!important; }

    .feat-pill { transition: background .14s ease; }
    .feat-pill:hover { background:rgba(255,255,255,0.08)!important; }

    .copy-btn { transition:all .14s ease; cursor:pointer; }
    .copy-btn:hover { background:#F1F5F9!important; }

    body,* { font-family:Verdana,Geneva,Tahoma,sans-serif!important; }
  `;
  document.head.appendChild(s);
}

/* ─────────────────────────────────────────────────────────────────
   Tokens
───────────────────────────────────────────────────────────────── */
const C = {
  bg:        '#F8FAFC',
  surface:   '#FFFFFF',
  border:    '#E2E8F0',
  text:      '#0F172A',
  textSub:   '#475569',
  textMuted: '#94A3B8',
  accent:    '#2563EB',
  accentBg:  '#EFF6FF',
  green:     '#16A34A',
  greenBg:   '#F0FDF4',
  red:       '#DC2626',
  redBg:     '#FEF2F2',
  amber:     '#D97706',
  ink:       '#0F172A',
  inkSub:    '#1E293B',
};
const F = `Verdana,Geneva,Tahoma,sans-serif`;

/* ─────────────────────────────────────────────────────────────────
   Platform config
───────────────────────────────────────────────────────────────── */
const PLATFORM = {
  youtube:   { label: 'YouTube',   color: '#E5001A', Icon: YouTubeIcon   },
  tiktok:    { label: 'TikTok',    color: '#2B2B2B', Icon: TikTokIcon    },
  instagram: { label: 'Instagram', color: '#C1348A', Icon: InstagramIcon },
  link:      { label: 'Link',      color: '#64748B', Icon: LinkIcon      },
};

function detectPlat(url = '') {
  const l = url.toLowerCase();
  if (l.includes('youtube.com') || l.includes('youtu.be')) return 'youtube';
  if (l.includes('tiktok.com'))    return 'tiktok';
  if (l.includes('instagram.com')) return 'instagram';
  return 'link';
}

function truncUrl(url = '', max = 48) {
  try {
    const u = new URL(url);
    const s = (u.hostname + u.pathname).replace(/^www\./, '');
    return s.length > max ? s.slice(0, max) + '…' : s;
  } catch { return url.slice(0, max); }
}

/* Extract a @handle or display name from a URL if possible */
function extractHandle(url = '', fallbackName = '') {
  try {
    const u = new URL(url);
    const parts = u.pathname.split('/').filter(Boolean);
    // TikTok: /@username  Instagram: /username  YouTube: /@handle or /c/name
    for (const p of parts) {
      if (p.startsWith('@')) return p;
    }
    // YouTube /c/ChannelName or /user/name
    const idx = parts.findIndex(p => p === 'c' || p === 'user' || p === 'channel');
    if (idx !== -1 && parts[idx + 1]) return `@${parts[idx + 1]}`;
    // Instagram first path segment
    if (u.hostname.includes('instagram') && parts[0]) return `@${parts[0]}`;
    // TikTok first path
    if (u.hostname.includes('tiktok') && parts[0]) return `@${parts[0].replace('@', '')}`;
  } catch {}
  return fallbackName ? `@${fallbackName.replace(/\s+/g, '').toLowerCase()}` : '';
}

/* ─────────────────────────────────────────────────────────────────
   Number formatting
───────────────────────────────────────────────────────────────── */
function fmt(n) {
  if (n == null || isNaN(Number(n))) return '—';
  const v = Number(n);
  if (v === 0) return '0';
  if (v >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return v.toLocaleString();
}

function statusCfg(s) {
  switch (s?.toLowerCase()) {
    case 'active':    return { label: 'Live',      color: C.green     };
    case 'completed': return { label: 'Completed', color: C.accent    };
    case 'archived':  return { label: 'Archived',  color: C.textMuted };
    default:          return { label: 'Live',      color: C.green     };
  }
}

/* ─────────────────────────────────────────────────────────────────
   Aggregate totals
   ─────────────────────────────────────────────────────────────────
   Priority: if trackerHistory has records, use the LATEST snapshot
   per URL across all URLs — that's the true current total.
   Otherwise fall back to whatever is on tracker.links directly.
───────────────────────────────────────────────────────────────── */
function computeTotals(links, historyData) {
  // If we have history, sum the MOST RECENT record per URL
  if (historyData && historyData.length > 0) {
    return historyData.reduce(
      (acc, group) => {
        if (!group.data || group.data.length === 0) return acc;
        // latest record (already sorted asc by backend, so last = latest)
        const latest = group.data[group.data.length - 1];
        acc.views    += Number(latest.view_count  || 0);
        acc.likes    += Number(latest.like_count  || 0);
        acc.comments += Number(latest.comments    || 0);
        return acc;
      },
      { views: 0, likes: 0, comments: 0 }
    );
  }

  // Fallback: snapshot on the links array itself
  return (links || []).reduce(
    (acc, l) => {
      acc.views    += Number(l?.views    ?? l?.view_count    ?? 0);
      acc.likes    += Number(l?.likes    ?? l?.like_count    ?? 0);
      acc.comments += Number(l?.comments ?? l?.comment_count ?? 0);
      return acc;
    },
    { views: 0, likes: 0, comments: 0 }
  );
}

/* ─────────────────────────────────────────────────────────────────
   Growth rate — week-over-week view delta across ALL URLs
   ─────────────────────────────────────────────────────────────────
   historyData shape (from backend):
   [ { id, url, data: [ { view_count, like_count, comments, shares, recorded_at }, ... ] } ]

   Strategy: for each URL find the latest record in [now-7d, now] and
   [now-14d, now-7d], sum across all URLs, compare.
───────────────────────────────────────────────────────────────── */
function computeGrowthRate(historyData) {
  if (!historyData || historyData.length === 0) return null;

  // Gather every record across all URLs
  const allRecords = historyData.flatMap(group =>
    (group.data || []).map(r => ({
      url:        group.url,
      view_count: Number(r.view_count || 0),
      recorded_at: new Date(r.recorded_at),
    }))
  ).filter(r => !isNaN(r.recorded_at));

  if (allRecords.length < 2) return null;

  allRecords.sort((a, b) => a.recorded_at - b.recorded_at);

  const newest  = allRecords[allRecords.length - 1].recorded_at;
  const cut1    = new Date(newest); cut1.setDate(cut1.getDate() - 7);   // -7d
  const cut2    = new Date(newest); cut2.setDate(cut2.getDate() - 14);  // -14d

  // For each URL: pick its latest record within each window
  const urls = [...new Set(allRecords.map(r => r.url))];

  let recentTotal = 0;
  let priorTotal  = 0;

  for (const url of urls) {
    const urlRecords = allRecords.filter(r => r.url === url);

    const recentRecords = urlRecords.filter(r => r.recorded_at >= cut1);
    const priorRecords  = urlRecords.filter(r => r.recorded_at >= cut2 && r.recorded_at < cut1);

    if (recentRecords.length) {
      recentTotal += recentRecords[recentRecords.length - 1].view_count;
    }
    if (priorRecords.length) {
      priorTotal  += priorRecords[priorRecords.length - 1].view_count;
    }
  }

  if (priorTotal === 0) return null;
  const pct = ((recentTotal - priorTotal) / priorTotal) * 100;
  return Math.round(pct * 10) / 10;
}

/* ─────────────────────────────────────────────────────────────────
   Per-link stats from history (latest snapshot for that URL)
───────────────────────────────────────────────────────────────── */
function getLatestStatsForUrl(url, historyData) {
  if (!historyData || historyData.length === 0) return null;
  const group = historyData.find(g => g.url === url);
  if (!group || !group.data || group.data.length === 0) return null;
  const latest = group.data[group.data.length - 1];
  return {
    views:    Number(latest.view_count || 0),
    likes:    Number(latest.like_count || 0),
    comments: Number(latest.comments   || 0),
  };
}

/* ─────────────────────────────────────────────────────────────────
   Platform aggregate compile
   Returns map: { youtube: {views,likes,comments}, tiktok: {...}, ... }
───────────────────────────────────────────────────────────────── */
function buildPlatformAggregates(links, historyData) {
  const agg = {};
  for (const l of links) {
    const url  = l?.link || '';
    const plat = detectPlat(url);
    if (!agg[plat]) agg[plat] = { views: 0, likes: 0, comments: 0, count: 0 };

    // Prefer history stats
    const hist = getLatestStatsForUrl(url, historyData);
    const v = hist ? hist.views    : Number(l?.views    ?? l?.view_count    ?? 0);
    const li= hist ? hist.likes    : Number(l?.likes    ?? l?.like_count    ?? 0);
    const co= hist ? hist.comments : Number(l?.comments ?? l?.comment_count ?? 0);

    agg[plat].views    += v;
    agg[plat].likes    += li;
    agg[plat].comments += co;
    agg[plat].count    += 1;
  }
  return agg;
}

/* ═══════════════════════════════════════════════════════════════
   SUB-COMPONENTS
══════════════════════════════════════════════════════════════════ */

/* ── GrowthBadge ── */
function GrowthBadge({ rate }) {
  if (rate == null) return null;
  const up    = rate > 0;
  const flat  = rate === 0;
  const Icon  = flat ? FlatIcon : up ? TrendingUpIcon : TrendingDownIcon;
  const color = flat ? C.textMuted : up ? C.green : C.red;
  const bg    = flat ? '#F1F5F9'   : up ? C.greenBg : C.redBg;
  const label = flat ? 'Flat' : `${up ? '+' : ''}${rate}%`;

  return (
    <Box className="anim-pop" sx={{
      display: 'inline-flex', alignItems: 'center', gap: '5px',
      px: '10px', py: '5px', borderRadius: '7px',
      bgcolor: bg, border: `1px solid ${color}22`,
    }}>
      <Icon sx={{ fontSize: 14, color }}/>
      <Typography sx={{ fontFamily: F, fontSize: '0.7rem', fontWeight: 700, color }}>
        {label}
      </Typography>
      <Typography sx={{ fontFamily: F, fontSize: '0.66rem', color: C.textMuted }}>
        views vs. last wk
      </Typography>
    </Box>
  );
}

/* ── StatCard ── */
function StatCard({ value, label, Icon, accent, delay }) {
  return (
    <Box className="stat-card anim-pop" style={{ animationDelay: `${delay}ms` }} sx={{
      flex: 1, minWidth: 0,
      bgcolor: C.surface,
      border: `1px solid ${C.border}`,
      borderRadius: '12px',
      p: { xs: '18px 20px', md: '22px 26px' },
      display: 'flex', alignItems: 'center', gap: '14px',
      boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
    }}>
      <Box sx={{
        width: 42, height: 42, flexShrink: 0,
        borderRadius: '10px',
        bgcolor: `${accent}14`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon sx={{ fontSize: 19, color: accent }}/>
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{
          fontFamily: F, fontWeight: 700,
          fontSize: { xs: '1.6rem', md: '2rem' },
          lineHeight: 1, color: C.text,
          letterSpacing: '-0.02em', mb: '4px',
        }}>
          {value}
        </Typography>
        <Typography sx={{
          fontFamily: F, fontSize: '0.66rem', fontWeight: 600,
          color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em',
        }}>
          {label}
        </Typography>
      </Box>
    </Box>
  );
}

/* ── PlatformCompileRow — shown when >1 platform, one row per platform ── */
function PlatformCompileRow({ plat, stats, delay }) {
  const meta = PLATFORM[plat];
  const { Icon } = meta;
  const iconColor = meta.color === '#000000' || meta.color === '#2B2B2B' ? '#444' : meta.color;

  return (
    <Box className="anim-pop" style={{ animationDelay: `${delay}ms` }} sx={{
      display: 'flex', alignItems: 'center',
      gap: { xs: '10px', md: '16px' },
      px: { xs: '14px', md: '20px' },
      py: '12px',
      bgcolor: C.surface,
      border: `1px solid ${C.border}`,
      borderRadius: '10px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
    }}>
      {/* Platform badge */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: '7px',
        minWidth: 110,
      }}>
        <Box sx={{
          width: 30, height: 30, borderRadius: '7px',
          bgcolor: `${meta.color}14`,
          border: `1px solid ${meta.color}22`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Icon sx={{ fontSize: 15, color: iconColor }}/>
        </Box>
        <Typography sx={{ fontFamily: F, fontSize: '0.75rem', fontWeight: 700, color: C.text }}>
          {meta.label}
        </Typography>
      </Box>

      {/* Divider */}
      <Box sx={{ width: 1, height: 28, bgcolor: C.border, flexShrink: 0 }}/>

      {/* Stats */}
      <Box sx={{ display: 'flex', flex: 1, gap: { xs: '16px', md: '32px' }, flexWrap: 'wrap' }}>
        {[
          { Icon: ViewsIcon,    val: stats.views,    label: 'Views'    },
          { Icon: LikesIcon,    val: stats.likes,    label: 'Likes'    },
          { Icon: CommentsIcon, val: stats.comments, label: 'Comments' },
        ].map(({ Icon: SI, val, label }) => (
          <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <SI sx={{ fontSize: 13, color: C.textMuted }}/>
            <Box>
              <Typography sx={{ fontFamily: F, fontSize: '0.8rem', fontWeight: 700, color: C.text, lineHeight: 1 }}>
                {fmt(val)}
              </Typography>
              <Typography sx={{ fontFamily: F, fontSize: '0.6rem', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {label}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      <Typography sx={{ fontFamily: F, fontSize: '0.66rem', color: C.textMuted, flexShrink: 0 }}>
        {stats.count} link{stats.count !== 1 ? 's' : ''}
      </Typography>
    </Box>
  );
}

/* ── LinkRow — individual link with @handle and history-sourced stats ── */
function LinkRow({ linkObj, index, delay, historyData, creatorName }) {
  const url     = linkObj?.link || '';
  const plat    = detectPlat(url);
  const meta    = PLATFORM[plat];
  const { Icon } = meta;
  const iconColor = meta.color === '#000000' || meta.color === '#2B2B2B' ? '#444' : meta.color;

  // Prefer history stats, fall back to snapshot on the link object
  const hist     = getLatestStatsForUrl(url, historyData);
  const views    = fmt(hist ? hist.views    : (linkObj?.views    ?? linkObj?.view_count    ?? null));
  const likes    = fmt(hist ? hist.likes    : (linkObj?.likes    ?? linkObj?.like_count    ?? null));
  const comments = fmt(hist ? hist.comments : (linkObj?.comments ?? linkObj?.comment_count ?? null));

  // @handle: try URL first, then fall back to creator_name
  const handle = extractHandle(url, creatorName);

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="link-row anim-up"
      style={{ textDecoration: 'none', display: 'block', animationDelay: `${delay}ms` }}
    >
      <Box sx={{
        display: 'flex', alignItems: 'center',
        gap: { xs: '10px', md: '18px' },
        px: { xs: '14px', md: '22px' },
        py: { xs: '13px', md: '15px' },
        bgcolor: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: '10px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
      }}>

        {/* Index */}
        <Typography sx={{
          fontFamily: F, fontWeight: 700, fontSize: '0.68rem',
          color: C.textMuted, width: 20, flexShrink: 0, textAlign: 'center',
        }}>
          {String(index + 1).padStart(2, '0')}
        </Typography>

        {/* Platform icon */}
        <Box sx={{
          width: 34, height: 34, flexShrink: 0,
          borderRadius: '8px',
          bgcolor: `${meta.color}12`,
          border: `1px solid ${meta.color}1E`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon sx={{ fontSize: 16, color: iconColor }}/>
        </Box>

        {/* URL + handle */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '2px', flexWrap: 'wrap' }}>
            <Typography sx={{
              fontFamily: F, fontSize: '0.74rem', fontWeight: 700,
              color: iconColor,
            }}>
              {meta.label}
            </Typography>
            {handle && (
              <Typography sx={{
                fontFamily: F, fontSize: '0.68rem', fontWeight: 600,
                color: C.textMuted,
                bgcolor: C.bg,
                border: `1px solid ${C.border}`,
                px: '6px', py: '1px', borderRadius: '4px',
                letterSpacing: '0.01em',
              }}>
                {handle}
              </Typography>
            )}
          </Box>
          <Typography sx={{
            fontFamily: F, fontSize: '0.7rem', color: C.textMuted,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {truncUrl(url)}
          </Typography>
        </Box>

        {/* Per-link stats */}
        <Box sx={{ display: 'flex', gap: { xs: '10px', md: '22px' }, flexShrink: 0, flexWrap: 'wrap' }}>
          {[
            { Icon: ViewsIcon,    val: views,    label: 'Views'    },
            { Icon: LikesIcon,    val: likes,    label: 'Likes'    },
            { Icon: CommentsIcon, val: comments, label: 'Comments' },
          ].map(({ Icon: SI, val, label }) => (
            <Box key={label} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: 48 }}>
              <Typography sx={{
                fontFamily: F, fontWeight: 700, fontSize: '0.78rem',
                color: val === '—' ? C.textMuted : C.text,
              }}>
                {val}
              </Typography>
              <Typography sx={{
                fontFamily: F, fontSize: '0.58rem', fontWeight: 600,
                color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em',
              }}>
                {label}
              </Typography>
            </Box>
          ))}
        </Box>

        <OpenInNewIcon sx={{ fontSize: 14, color: C.textMuted, flexShrink: 0 }}/>
      </Box>
    </a>
  );
}

/* ── Loading / Error shells ── */
function LoadingShell() {
  return (
    <ThemeProvider theme={lightTheme}><CssBaseline/>
      <HomeHeader/><Toolbar/>
      <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2, bgcolor: C.bg }}>
        <CircularProgress size={34} thickness={3} sx={{ color: C.accent }}/>
        <Typography sx={{ fontFamily: F, color: C.textMuted, fontSize: '0.78rem' }}>Loading tracker…</Typography>
      </Box>
    </ThemeProvider>
  );
}

function ErrorShell({ navigate }) {
  return (
    <ThemeProvider theme={lightTheme}><CssBaseline/>
      <HomeHeader/><Toolbar/>
      <Box sx={{ minHeight: '60vh', bgcolor: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Container maxWidth="sm" sx={{ textAlign: 'center', py: 10 }}>
          <Box sx={{ width: 52, height: 52, borderRadius: '12px', bgcolor: C.surface, border: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
            <img src={blitzLogo} alt="Blitz" style={{ width: 28, height: 28, objectFit: 'contain' }}/>
          </Box>
          <Typography sx={{ fontFamily: F, fontWeight: 700, fontSize: '1.35rem', color: C.text, mb: 1 }}>Tracker not found</Typography>
          <Typography sx={{ fontFamily: F, color: C.textMuted, lineHeight: 1.7, mb: 4, fontSize: '0.82rem' }}>
            This tracker may be private, archived, or the link may have changed.
          </Typography>
          <Button onClick={() => navigate('/')} sx={{ borderRadius: '8px', px: 3, py: 1.2, bgcolor: C.text, color: '#FFF', fontFamily: F, fontWeight: 700, fontSize: '0.78rem', textTransform: 'none', '&:hover': { bgcolor: C.inkSub } }}>
            Back to Blitz
          </Button>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

/* ── Blitz features ── */
const FEATURES = [
  { Icon: CreditCardIcon,  label: 'Instant Payments',  sub: 'BlitzPay in seconds'       },
  { Icon: BankIcon,        label: 'Banking & Balances', sub: 'USDC + ACH infra'          },
  { Icon: ContractIcon,    label: 'Smart Contracts',    sub: 'Sign & track agreements'   },
  { Icon: TrackerIconMui,  label: 'Campaign Trackers',  sub: 'Live performance data'     },
  { Icon: SearchIcon,      label: 'Find Creators',      sub: 'Global searchable roster'  },
  { Icon: ClipperIcon,     label: 'Clippers & Editors', sub: 'Contractors ready to hire' },
];

/* ═══════════════════════════════════════════════════════════════
   MAIN
══════════════════════════════════════════════════════════════════ */
export default function TrackerPublicPage() {
  useEffect(() => { ensureAssets(); }, []);

  const { trackerId } = useParams();
  const navigate      = useNavigate();

  const { data: tracker, isLoading, isError } = useQuery(
    ['trackerPublic', trackerId],
    () => client.trackers.getPublic(trackerId),
    { retry: 1, refetchOnWindowFocus: false, staleTime: 60_000 }
  );

  const [trackerHistory, setTrackerHistory] = useState([]);
  useEffect(() => {
    if (!trackerId) return;
    client.trackers.getHistoryPublic(trackerId)
      .then(d => setTrackerHistory(d || []))
      .catch(() => {});
  }, [trackerId]);

  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const links      = tracker?.links || [];
  const totalLinks = links.length;

  // Totals from history (authoritative) or fallback to link snapshots
  const totals = useMemo(
    () => computeTotals(links, trackerHistory),
    [links, trackerHistory]
  );

  // Platform breakdown counts for meta pills
  const platCounts = useMemo(() =>
    links.reduce((acc, l) => {
      const p = detectPlat(l?.link || '');
      if (p !== 'link') acc[p] = (acc[p] || 0) + 1;
      return acc;
    }, {}),
    [links]
  );

  // Per-platform aggregated stats (for compile section)
  const platAgg = useMemo(
    () => buildPlatformAggregates(links, trackerHistory),
    [links, trackerHistory]
  );
  const multiPlatform = Object.keys(platAgg).length > 1;

  // Growth rate from history
  const growthRate = useMemo(() => computeGrowthRate(trackerHistory), [trackerHistory]);
  const hasHistory = trackerHistory.length > 0;

  const sc = tracker ? statusCfg(tracker.status) : null;
  const creatorName = tracker?.creator_name || '';

  if (isLoading) return <LoadingShell/>;
  if (isError || !tracker) return <ErrorShell navigate={navigate}/>;

  return (
    <ThemeProvider theme={lightTheme}>
      <CssBaseline/>
      <PageTitle title={`${tracker.name} — Blitz Tracker`}/>
      <HomeHeader/>
      <Toolbar/>

      {/* ══ HEADER ══ */}
      {tracker.tracker_photo ? (
        <Box sx={{ position: 'relative', height: { xs: 190, md: 250 }, overflow: 'hidden', bgcolor: '#000' }}>
          <Box component="img" src={tracker.tracker_photo} alt={tracker.name}
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.4 }}
          />
          <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, transparent 20%, rgba(0,0,0,0.78) 100%)' }}/>
          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', alignItems: 'flex-end', pb: 3 }}>
            <Typography className="anim-up" sx={{ fontFamily: F, fontWeight: 700, fontSize: { xs: '1.5rem', md: '2.2rem' }, color: '#FFF', letterSpacing: '-0.01em' }}>
              {tracker.name}
            </Typography>
          </Container>
        </Box>
      ) : (
        <Box sx={{ bgcolor: C.surface, borderBottom: `1px solid ${C.border}` }}>
          <Container maxWidth="lg" sx={{ py: { xs: 3.5, md: 4.5 } }}>
            <Box className="anim-up" sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography sx={{ fontFamily: F, fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: C.accent, mb: '6px' }}>
                  Campaign Tracker
                </Typography>
                <Typography sx={{ fontFamily: F, fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.9rem' }, color: C.text, letterSpacing: '-0.015em', lineHeight: 1.2 }}>
                  {tracker.name}
                </Typography>
                {creatorName && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mt: '6px' }}>
                    <PersonIcon sx={{ fontSize: 13, color: C.textMuted }}/>
                    <Typography sx={{ fontFamily: F, fontSize: '0.72rem', color: C.textMuted }}>
                      {creatorName}
                    </Typography>
                  </Box>
                )}
                {tracker.description && (
                  <Typography sx={{ fontFamily: F, fontSize: '0.8rem', color: C.textSub, mt: 1, maxWidth: 580, lineHeight: 1.65 }}>
                    {tracker.description}
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', sm: 'flex-end' }, gap: '7px' }}>
                {/* Status */}
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px', px: '9px', py: '5px', borderRadius: '6px', bgcolor: `${sc.color}10`, border: `1px solid ${sc.color}22` }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: sc.color, ...(sc.label === 'Live' && { animation: 'tpp-live 1.8s ease infinite', boxShadow: `0 0 4px ${sc.color}` }) }}/>
                  <Typography sx={{ fontFamily: F, fontSize: '0.66rem', fontWeight: 700, color: sc.color }}>{sc.label}</Typography>
                </Box>

                {/* Share */}
                <Box className="copy-btn" onClick={handleCopy} sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px', px: '9px', py: '5px', borderRadius: '6px', bgcolor: copied ? C.accent : C.bg, border: `1px solid ${copied ? C.accent : C.border}` }}>
                  <ShareIcon sx={{ fontSize: 11, color: copied ? '#FFF' : C.textMuted }}/>
                  <Typography sx={{ fontFamily: F, fontSize: '0.66rem', fontWeight: 700, color: copied ? '#FFF' : C.textMuted }}>
                    {copied ? 'Copied!' : 'Share tracker'}
                  </Typography>
                </Box>

                <Typography sx={{ fontFamily: F, fontSize: '0.65rem', color: C.textMuted }}>
                  {new Date(tracker.date_created || 0).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </Typography>
              </Box>
            </Box>
          </Container>
        </Box>
      )}

      {/* ══ BODY ══ */}
      <Box sx={{ bgcolor: C.bg, minHeight: '60vh' }}>
        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 5.5 } }}>

          {/* Description below cover photo */}
          {tracker.tracker_photo && tracker.description && (
            <Typography className="anim-up" sx={{ fontFamily: F, fontSize: '0.82rem', color: C.textSub, mb: 3.5, maxWidth: 620, lineHeight: 1.7 }}>
              {tracker.description}
            </Typography>
          )}

          {/* ════ SECTION 1: Total Performance ════ */}
          <Box className="anim-in" sx={{ mb: { xs: 3, md: 4 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: 2.5 }}>
              <Typography sx={{ fontFamily: F, fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: C.textMuted, whiteSpace: 'nowrap' }}>
                Total Performance
              </Typography>
              <Box sx={{ flex: 1, height: 1, bgcolor: C.border }}/>
              <GrowthBadge rate={growthRate}/>
            </Box>

            <Box sx={{ display: 'flex', gap: { xs: '8px', md: '12px' }, flexWrap: 'wrap', mb: 2 }}>
              <StatCard value={fmt(totals.views)}    label="Total Views"    Icon={ViewsIcon}    accent={C.accent}   delay={0}/>
              <StatCard value={fmt(totals.likes)}    label="Total Likes"    Icon={LikesIcon}    accent="#E11D48"    delay={55}/>
              <StatCard value={fmt(totals.comments)} label="Total Comments" Icon={CommentsIcon} accent={C.amber}    delay={110}/>
            </Box>

            {/* Meta pills */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '7px' }}>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px', px: '9px', py: '4px', borderRadius: '6px', bgcolor: C.surface, border: `1px solid ${C.border}` }}>
                <LinkIcon sx={{ fontSize: 11, color: C.textMuted }}/>
                <Typography sx={{ fontFamily: F, fontSize: '0.68rem', fontWeight: 600, color: C.textSub }}>
                  {totalLinks} link{totalLinks !== 1 ? 's' : ''} tracked
                </Typography>
              </Box>
              {Object.entries(platCounts).map(([p, n]) => {
                const m = PLATFORM[p];
                const ic = m.color === '#000000' || m.color === '#2B2B2B' ? '#444' : m.color;
                return (
                  <Box key={p} sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px', px: '9px', py: '4px', borderRadius: '6px', bgcolor: C.surface, border: `1px solid ${C.border}` }}>
                    <m.Icon sx={{ fontSize: 11, color: ic }}/>
                    <Typography sx={{ fontFamily: F, fontSize: '0.68rem', fontWeight: 600, color: C.textSub }}>
                      {n} {m.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Box sx={{ height: 1, bgcolor: C.border, mb: { xs: 3.5, md: 4.5 } }}/>

          {/* ════ SECTION 2: Performance Chart ════ */}
          {hasHistory && (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontFamily: F, fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: C.textMuted, mb: 1.5 }}>
                  Growth Over Time
                </Typography>
                <Box sx={{ bgcolor: C.surface, border: `1px solid ${C.border}`, borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                  <TrackerProgressChart historyData={trackerHistory}/>
                </Box>
              </Box>
              <Box sx={{ height: 1, bgcolor: C.border, mt: { xs: 3.5, md: 4.5 }, mb: { xs: 3.5, md: 4.5 } }}/>
            </>
          )}

          {/* ════ SECTION 3: Platform Compile (multi-platform only) ════ */}
          {multiPlatform && totalLinks > 0 && (
            <>
              <Box sx={{ mb: { xs: 3, md: 4 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: 2 }}>
                  <BarChartIcon sx={{ fontSize: 14, color: C.textMuted }}/>
                  <Typography sx={{ fontFamily: F, fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: C.textMuted }}>
                    By Platform
                  </Typography>
                  <Box sx={{ flex: 1, height: 1, bgcolor: C.border }}/>
                </Box>
                <Stack spacing={1.2}>
                  {Object.entries(platAgg).map(([plat, stats], i) => (
                    <PlatformCompileRow key={plat} plat={plat} stats={stats} delay={i * 50}/>
                  ))}
                </Stack>
              </Box>
              <Box sx={{ height: 1, bgcolor: C.border, mb: { xs: 3.5, md: 4.5 } }}/>
            </>
          )}

          {/* ════ SECTION 4: Individual Links ════ */}
          <Box sx={{ mb: { xs: 4, md: 5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: 2 }}>
              <Typography sx={{ fontFamily: F, fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: C.textMuted }}>
                Tracked Links
              </Typography>
              <Box sx={{ px: '7px', py: '2px', borderRadius: '5px', bgcolor: C.accentBg, border: `1px solid ${C.accent}22` }}>
                <Typography sx={{ fontFamily: F, fontSize: '0.66rem', fontWeight: 700, color: C.accent }}>{totalLinks}</Typography>
              </Box>
              <Box sx={{ flex: 1, height: 1, bgcolor: C.border }}/>
            </Box>

            {totalLinks > 0 ? (
              <Stack spacing={1.2}>
                {links.map((l, i) => (
                  <LinkRow
                    key={i}
                    linkObj={l}
                    index={i}
                    delay={50 + i * 28}
                    historyData={trackerHistory}
                    creatorName={creatorName}
                  />
                ))}
              </Stack>
            ) : (
              <Box sx={{ py: 8, textAlign: 'center', bgcolor: C.surface, border: `1px dashed ${C.border}`, borderRadius: '10px' }}>
                <LinkIcon sx={{ fontSize: 22, color: C.border, mb: 1.5 }}/>
                <Typography sx={{ fontFamily: F, color: C.textMuted, fontSize: '0.8rem' }}>No links tracked yet</Typography>
              </Box>
            )}
          </Box>
        </Container>
      </Box>

      {/* ══ BLITZ BRAND ══ */}
      <Box sx={{ bgcolor: C.ink, position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ height: '2px', background: `linear-gradient(90deg, transparent, ${C.accent}, transparent)` }}/>
        <Container maxWidth="lg" sx={{ py: { xs: 5.5, md: 7.5 } }}>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' }, gap: { xs: 3, md: 5 }, mb: 5.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '11px', flexShrink: 0 }}>
              <Box sx={{ width: 42, height: 42, borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', p: '8px' }}>
                <img src={blitzLogo} alt="Blitz" style={{ width: '100%', height: '100%', objectFit: 'contain' }}/>
              </Box>
              <Typography sx={{ fontFamily: F, fontWeight: 700, fontSize: '1.2rem', color: '#FFF', letterSpacing: '-0.01em' }}>
                Blitz
              </Typography>
            </Box>

            <Box sx={{ flex: 1, maxWidth: 500 }}>
              <Typography sx={{ fontFamily: F, fontWeight: 700, fontSize: { xs: '1.1rem', md: '1.35rem' }, color: '#FFF', lineHeight: 1.35, letterSpacing: '-0.01em', mb: '7px' }}>
                Payments, Banking, Contracts & Trackers — built for the creator economy.
              </Typography>
              <Typography sx={{ fontFamily: F, fontSize: '0.78rem', color: 'rgb(255, 255, 255)', lineHeight: 1.7 }}>
                Used by teams all over the world.
              </Typography>
            </Box>

            <Button
              component="a" href="https://www.useblitz.co" target="_blank" rel="noopener noreferrer"
              endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }}/>}
              sx={{ flexShrink: 0, borderRadius: '8px', px: '20px', py: '10px', bgcolor: C.accent, color: '#FFF', fontFamily: F, fontWeight: 700, fontSize: '0.78rem', textTransform: 'none', boxShadow: `0 4px 16px ${C.accent}44`, transition: 'all .15s ease', '&:hover': { bgcolor: '#1D4ED8', transform: 'translateY(-1px)', boxShadow: `0 6px 24px ${C.accent}55` } }}
            >
              Get started free
            </Button>
          </Box>

          <Grid container spacing={1.2} sx={{ mb: 4.5 }}>
            {FEATURES.map((f) => (
              <Grid item xs={6} sm={4} md={2} key={f.label}>
                <Box className="feat-pill" sx={{ p: '14px 12px', borderRadius: '9px', border: '1px solid rgba(255,255,255)', bgcolor: 'rgba(255,255,255,0.03)', height: '100%' }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: '7px', bgcolor: `${C.accent}18`, border: `1px solid ${C.accent}24`, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: '9px' }}>
                    <f.Icon sx={{ fontSize: 15, color: '#60A5FA' }}/>
                  </Box>
                  <Typography sx={{ fontFamily: F, fontWeight: 700, fontSize: '0.72rem', color: '#FFF', mb: '3px' }}>{f.label}</Typography>
                  <Typography sx={{ fontFamily: F, fontSize: '0.65rem', color: 'rgba(255,255,255,0.35)', lineHeight: 1.5 }}>{f.sub}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.07)', pt: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Typography sx={{ fontFamily: F, fontSize: '0.6rem', color: 'rgba(255,255,255)', letterSpacing: '0.05em' }}>
              Blitz Systems Corp · Tracker powered by Blitz
            </Typography>
            <Typography sx={{ fontFamily: F, fontSize: '0.6rem', color: 'rgba(255,255,255)', letterSpacing: '0.04em' }}>
              useblitz.co
            </Typography>
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
}